// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { LoadingStatus, AlertType } from '@app/core/models';
import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  Output,
  EventEmitter,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef
} from '@angular/core';
import { API_KEY, QueueAlertAction } from '@app/core';
import { Observable, BehaviorSubject, EMPTY, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, take, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { State } from '@app/access-management/state';

@Component({
  selector: 'app-data-table-ajax',
  templateUrl: './data-table-ajax.component.html',
  styleUrls: ['./data-table-ajax.component.scss']
})
export class DataTableAjaxComponent implements OnDestroy, OnChanges, OnInit {
  _rows: any[] = [];
  _rowCount = 0;
  _initialSelectedItems: any[] = [];
  _maxPageNumber = 2;

  @Input() url: any;
  @Input() responseKey: any;
  @Input() limit = 10;
  @Input() columns: any;
  @Input() columnMode = 'flex';
  @Input() displaySearch = true;
  @Input() extendRow = false;
  @Input() rowHeight = 60;
  @Input() selectedItemKey = 'id';
  @Input() selectionType = 'single';
  @Input() selectedItems: any[] = [];
  @Input() template!: TemplateRef<any>;
  @Input() excludedItems: any[] = [];
  @Input() displayPagination = true;

  @Output() rowClicked = new EventEmitter<SelectedRows>();
  @ViewChild('myTable') table: any;

  search = '';
  currentPageNumber = 0;
  filteredList: any[] = [];
  fullList: any[] = [];
  expanded: any = {};
  timeout: any;

  loading = { status: LoadingStatus.LOADING };
  loading$ = new BehaviorSubject<any>(this.loading);

  constructor(private store: Store<State>, private httpClient: HttpClient, @Inject(API_KEY) private api: string) {}

  toggleExpandRow(row: any) {
    this.table.rowDetail.toggleExpandRow(row);
  }

  searchValues(data: string) {
    this.search = data;
    this.updatePagination(-this.currentPageNumber, 'search');
  }

  trackColumns(id: any) {
    return id;
  }

  rowIdentity = (row: any) => row[this.selectedItemKey];

  isExcludedRow(row: any) {
    if (!this.excludedItems || !this.excludedItems) {
      return false;
    }

    return this.excludedItems.find(i => i[this.selectedItemKey] === row[this.selectedItemKey]);
  }

  ngOnInit() {
    this._initialSelectedItems = [...this.selectedItems];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['url'] && changes['url'].currentValue) {
      this.updatePagination(0, 'page');
      this.selectedItems = [...this._initialSelectedItems];
    }
  }

  ngOnDestroy() {
    this.loading$.unsubscribe();
  }

  selectRow(row: SelectedRows) {
    row.selected.map((excluRow, index) => {
      if (this.isExcludedRow(excluRow)) {
        row.selected.splice(index, 1);
      }
    });
    this.rowClicked.emit(row);
  }

  refreshTable() {
    this.updatePagination(0, 'page');
  }

  updatePagination(move: number, type: string) {
    this.currentPageNumber += move;
    if (this.currentPageNumber < 0) {
      this.currentPageNumber = 0;
      return;
    }

    const offset = this.currentPageNumber * this.limit;
    const _previousList = this._rows;

    this.loading$.next(this.loading);
    this._rowCount = this.limit;

    this.getPage(offset, this.limit, this.search).subscribe(
      (list: any) => {
        this.loading$.next(null);
        this._rows = list;
        this._rowCount = list && list.length;
        this.allowNextPage();

        if (!list || !list.length || list.length < this.limit) {
          this.markAsMaxPage();
        }
        this.handleNextPageError(_previousList, type);
      },
      err => {
        if (type === 'search') {
          this.markAsMaxPage();
          this._rows = [];
        }
      }
    );
  }

  handleNextPageError(_previousList: any, type: string) {
    if (this.currentPageNumber > 0 && (!this._rows || !this._rows.length)) {
      this.currentPageNumber -= 1;
      this._rows = _previousList;
      this._rowCount = _previousList.length;
      this.markAsMaxPage();
    }
  }

  allowNextPage() {
    this._maxPageNumber = this.currentPageNumber + 1;
  }

  markAsMaxPage() {
    this._maxPageNumber = this.currentPageNumber;
  }

  displayNoMoreItemsAlert() {
    this.store.dispatch(
      new QueueAlertAction({
        alertText: `There is no items in the next page.`,
        alertType: AlertType.ERROR
      })
    );
  }

  displayErrorGettingPage(err: any) {
    this.loading$.next(null);
    this.store.dispatch(
      new QueueAlertAction({
        alertText: `There was an error fetching your data.`,
        alertType: AlertType.ERROR
      })
    );
    return throwError(err);
  }

  resetPagination() {
    this.currentPageNumber = 0;
  }

  getPage(offset: number = 0, limit: number = this.limit, search: string = this.search): Observable<any[]> {
    if (!this.url) {
      throw new Error('API URL for pagination is not defined');
    }
    const route = `${this.api}${this.url}?offset=${offset}&limit=${limit}&search=${encodeURIComponent(search)}`;
    return this.httpClient.get(route).pipe(
      take(1),
      map((response: any) => (this.responseKey ? response[this.responseKey] : response)),
      catchError(err => this.displayErrorGettingPage(err))
    );
  }
}

export interface SelectedRows {
  selected: any[];
}



// WEBPACK FOOTER //
// ./src/app/shared/data-table-ajax/data-table-ajax.component.ts